import { useEffect, useState } from 'react';
import { apiGet, type CustomRequestOptions } from '../js/api';
import _server from '../js/config/_server';
import queryString from 'query-string';
import { mergeDeepRight } from 'ramda';

const defaultErrorMessage = 'Failed to load resource.';

type AnyFunction = (...args: any) => any;

type RequiredOptions = CustomRequestOptions;

type OptionalOptions<F> = {
    dataFormatter: F extends AnyFunction ? F : never;
};

type Options<F> = RequiredOptions & OptionalOptions<F>;

export function useFetch<Resource, Formatter = never>(
    source: string,
    options: Options<Formatter>,
    errorMessage?: string
) {
    const [data, setData] =
        useState<
            Formatter extends AnyFunction
                ? ReturnType<typeof options.dataFormatter>
                : Resource
        >();

    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = async (options: Options<Formatter>) => {
        setIsLoading(true);

        const { url, query } = queryString.parseUrl(_server.baseUrl + source);

        try {
            const res = await apiGet<Resource>(url, {
                ...options,
                params: mergeDeepRight(query as any, options?.params ?? {})
            });

            if (options.dataFormatter) {
                setData(options.dataFormatter(res));
            } else {
                setData(() => res as never);
            }
            setIsLoading(false);
        } catch (error) {
            setError(errorMessage || defaultErrorMessage);
            console.warn(error);

            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData(options);
    }, []);

    return { data, error, isLoading, refetchData: fetchData };
}
